import { useEffect, useState } from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/index';

import { useSpecialtiesByPractice } from '@/react-query/useSpecialtiesByPractice';
import { useAppContext } from '@/utils/hooks/useAppContext';

interface SpecialtySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (specialtyId: number) => void;
  isLoading?: boolean;
}

export const SpecialtySelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: SpecialtySelectionModalProps) => {
  const { t } = useLingui();
  const { user } = useAppContext();
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string>('');

  const practiceId = user?.primaryPracticeId;

  const { data: specialties } = useSpecialtiesByPractice(practiceId || '', {
    enabled: !!practiceId && practiceId !== '',
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedSpecialtyId('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedSpecialtyId) {
      onConfirm(parseInt(selectedSpecialtyId));
    }
  };

  const handleClose = () => {
    setSelectedSpecialtyId('');
    onClose();
  };

  const isConfirmDisabled = !selectedSpecialtyId || isLoading;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent className="vai-max-w-md vai-p-6">
        <DialogHeader className="vai-pb-4">
          <DialogTitle>
            <Trans>Select Specialty for Referral</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="vai-space-y-4">
          <div className="vai-space-y-2">
            <div className="vai-text-sm vai-font-medium vai-text-gray-700">
              <Trans>Specialty</Trans>
            </div>
            {!specialties || specialties.length === 0 ? (
              <div className="vai-flex vai-items-center vai-justify-center vai-h-10 vai-border vai-border-gray-300 vai-rounded-md vai-bg-gray-50 vai-text-gray-600 vai-text-sm">
                <Trans>No specialties available for this practice.</Trans>
              </div>
            ) : (
              <Select
                value={selectedSpecialtyId}
                onValueChange={setSelectedSpecialtyId}
                disabled={isLoading}
              >
                <SelectTrigger className="vai-w-full">
                  <SelectValue placeholder={t`Select a specialty...`} />
                </SelectTrigger>
                <SelectContent>
                  {specialties?.map((specialty) => (
                    <SelectItem
                      key={specialty.id}
                      value={specialty.id.toString()}
                    >
                      {specialty.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="vai-text-sm vai-text-gray-600">
            <Trans>
              Please select a specialty to flag this referral. The referral will
              be marked as &ldquo;Needs Referral&rdquo; with the selected
              specialty.
            </Trans>
          </div>
        </div>

        <div className="vai-flex vai-justify-end vai-gap-3 vai-pt-4 vai-border-t">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className="vai-min-w-[100px]"
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="vai-mr-2 vai-h-3 vai-w-3 vai-animate-spin"
                />
                <Trans>Processing...</Trans>
              </>
            ) : (
              <Trans>Confirm</Trans>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
